import React, { useEffect, useRef, useState } from "react";

import Page, { mmToPixel } from "../models/Page";
import DrawingArea from "./DrawingArea";
import "./PageView.css";

import Point from "../models/Point";
import { getChildElement } from "../modelViews/GraficElementUtils";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import PageFrame from "./PageFrame";

import { showToast } from "./Toasts";

import Logger from "../Logger";
import Browser from "../browser";
import Zoomer from "../utils/Zoomer";
import { useCommand } from "../models/useCommand";
import ActivePanes from "../models/ActivePanes";
import CabinetView from "../modelViews/CabinetView";
import SceneGroupView from "../modelViews/SceneGroupView";
import BackgroundImage from "./DrawingBackgroundImage";
import AdjustBackgroundImage from "./AdjustBackgroundImage";
import BackgroundImagePosition from "../models/BackgroundImagePosition";
import ButtonChannelsView from "../modelViews/ButtonChannelsView";
import BoundaryRect from "../models/BoundaryRect";
import { RenderTargetProvider, RenderTarget } from "./renderPrimitives/renderTarget";
import { RDiv } from "./renderPrimitives/RDiv";

import DrawingItemEditContainer from "./DrawingItemEditContainer";
import { pageTypeInfo, PageTypes } from "../models/PageTypes";
import BackgroundImagePdf from "./DrawingBackgroundImagePdf";

const logger = new Logger();

const cmToPixel = 37.79527559055118;

const BACKGROUND_IDS = ["pageShadowWrapper", "mainDrawingArea", "pageContainer"];

/////////////////////////////////////////////////////

function getBackgroundImageElement(page: Page): HTMLElement | undefined {
  var res: HTMLElement | undefined;
  const pageElment = document.getElementById(page.id);
  if (pageElment) {
    res = getChildElement(pageElment, "backgroundImageWrapper") as HTMLElement;
  }
  return res;
}

export function PageContent(props: { page: Page }) {
  const { page } = props;

  var pageContent: any | undefined;

  if (page.isRoomOrFloorPlan) {
    pageContent = (
      <RDiv
        className="pageContent"
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
        }}
      >
        <BackgroundImage page={page} />
        {/* <BackgroundImagePdf page={page} /> */}
        <DrawingArea page={page} />
        {/* <DebugGrid /> */}
      </RDiv>
    );
  }
  return pageContent;
}

export default function PageView(props: { page: Page; activePanes: ActivePanes }) {
  const { page, activePanes } = props;

  const maxZoomFactor = page.parent?.className == "Floor" ? 20 : 20;

  function getCurrentZoomLevel(): number {
    const s = pageRef.current?.getAttribute("data-scale"); // no itx
    if (s) {
      return parseFloat(s);
    }

    return 1;

    // return page.currentZoom.value;
  }

  const shadowWrapperPadding = 0; // 0.5; // cm

  const pageRef = useRef<HTMLDivElement>(null);
  const pageShadowWrapperRef = useRef<HTMLDivElement>(null);

  const touchZoomRef = useRef<HTMLDivElement>(null);

  const zoomSliderRef = useRef<HTMLInputElement>(null);

  const pageOuterRef = useRef<HTMLDivElement>(null);
  const pageClipperRef = useRef<HTMLDivElement>(null);

  const pageScrollerRef = useRef<HTMLDivElement>(null);
  const pageScrollerContentRef = useRef<HTMLDivElement>(null);
  const pageScrollerMouseDown = useRef<boolean>(false);
  const zoomFrameRef = useRef<number | undefined>(undefined);

  const sheetNumberRef = useRef<HTMLInputElement>(null);

  const [sheetNumber, setSheetNumber] = useState<number>(1);

  const minZoom = useRef(0.2);
  const initialPos = useRef(new BackgroundImagePosition());

  useOnChangeWatcher(page.fixedCabinetView);

  useOnChangeWatcher(state.uiMode);

  logger.log(`render page`);

  useCommand("activePane", (data: any) => {
    if (data != `pane_${page.id}`) {
      console.log("activate pageview ", data);
      page.updateConnections();
      return true;
    }
    return false;
  });

  useCommand("deactivePane", (data: any) => {
    return false;
  });

  //
  // Dieser Event wir NUR auf ECHTEN Touch-Devices gefeuert
  // und nicht auf dem Desktop mit Touchpad
  //

  function onTouch(event: any) {
    if (page.adjustActive.value) {
      event.stopPropagation();
    }
    console.log("touch event", event);
    Zoomer.touchStart(event);
  }

  useEffect(() => {
    calcMinZoom();

    state.showButtonChannels.value = undefined; // close dialog on page load

    if (true) {
      // isNaN(page.currentZoom.value)) {
      page.currentZoom.value = minZoom.current;
      if (pageRef.current) {
        pageRef.current.setAttribute("data-scale", `${minZoom.current}`); // no itx
      }
    }

    setZoom(page.currentZoom.value);
    applyNewZoomLevel(new Point(0, 0), page.currentZoom.value);

    pageOuterRef.current?.scrollTo(page.currentPan.value.x, page.currentPan.value.y);

    page.screen2 = activePanes.screen2;

    // MWE: 1.8.2025
    //
    // onWwheel nuß hier registriert werden, da sonst der Event nicht als "aktiv" gesetzt ist,
    // somit kein preventDefault() aufgerufen werden kann
    // mit dem Ergebniss, dass die gesamte Seite gezoomt wird
    //
    pageOuterRef.current?.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      if (zoomFrameRef.current !== undefined) {
        cancelAnimationFrame(zoomFrameRef.current);
        zoomFrameRef.current = undefined;
      }
      pageOuterRef.current?.removeEventListener("wheel", onWheel);
    };
  }, []);

  useEffect(() => {
    const element = pageScrollerRef.current;
    if (element && pageClipperRef.current) {
      let scrollbarWidth = 0;
      scrollbarWidth = element.offsetWidth - element.clientWidth;
      let scrollbarHeight = scrollbarWidth;

      pageClipperRef.current.style.clipPath = `inset(0px ${scrollbarWidth}px ${scrollbarHeight}px 0px)`;
    }
  });

  // Panning logic
  const mouseIsDown = useRef(false);
  const isPanning = useRef(false);
  const lastX = useRef(0);
  const lastY = useRef(0);

  const startX = useRef(0);
  const startY = useRef(0);

  const ignoreClick = useRef(false);

  const offset = useRef({ x: 0, y: 0 });

  function onClick(e: any) {
    if (ignoreClick.current) return;
    if (BACKGROUND_IDS.includes(e.target.id)) {
      state.showButtonChannels.value = undefined; // close dialog on click outside
      state.currentEditObject.value = undefined;

      state.selectionManager.selectedObject = undefined;
      page.connections.forEach((connection) => {
        connection.selected = false;
        connection.setIsHovered(false);
        connection.setIsHighlighted(false);
      });
    }
  }

  const handlePanPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (page.adjustActive.value) return;

    ignoreClick.current = false;

    if (!BACKGROUND_IDS.includes((event.target as any).id)) {
      return;
    }

    if (event.target instanceof Element && !event.target.closest(".pane")) return;

    if (event.button !== 0) return; // Only left mouse button

    isPanning.current = false;
    mouseIsDown.current = true;

    startX.current = lastX.current = event.clientX;
    startY.current = lastY.current = event.clientY;

    if (pageOuterRef.current) {
      pageOuterRef.current.setPointerCapture(event.pointerId);
    }

    // document.addEventListener("mouseup", handlePanMouseUp);
  };

  const handlePanPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (page.adjustActive.value) return;

    if (!mouseIsDown.current) return;

    if (e.buttons == 0) {
      mouseIsDown.current = false;
      return;
    }

    e.stopPropagation();

    const eps = 10;
    if (Math.abs(startX.current - e.clientX) > eps || Math.abs(startY.current - e.clientY) > eps) {
      isPanning.current = true;
      ignoreClick.current = true;
    }

    const dx = e.clientX - lastX.current;
    const dy = e.clientY - lastY.current;

    if (pageOuterRef.current) {
      offset.current.x += dx;
      offset.current.y += dy;

      const x1 = offset.current.x;
      const y1 = offset.current.y;
      limitOffset();

      const dx1 = offset.current.x - x1;
      const dy1 = offset.current.y - y1;

      lastX.current = e.clientX + dx1;
      lastY.current = e.clientY + dy1;

      removeTransition();
      setTransformationInStyle();
      setSizeAndPositionForPageScroller();
      setTransition();

      const lx = page.currentPan.value.x;
      const ly = page.currentPan.value.y;

      page.recalcConnections.value++;
      page.currentPan.value = new Point(pageOuterRef.current.scrollLeft, pageOuterRef.current.scrollTop);

      if (state.currentEditObject.value) {
        const p = state.currentEditObject.value.getScreenPosition(page);
        state.currentEditObjectPosition.value = p;
      }

      pageOuterRef.current.style.cursor = "grabbing"; // no itx
    }
  };

  const handlePanPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pageOuterRef.current) {
      pageOuterRef.current.releasePointerCapture(event.pointerId);
    }

    if (page.adjustActive.value) return;

    if (!mouseIsDown.current) return;

    event.stopPropagation();

    // document.removeEventListener("mouseup", handlePanMouseUp);
    if (pageOuterRef.current) {
      pageOuterRef.current.style.cursor = "default";
    }

    if ((event.target as HTMLElement).id == "mainDrawingArea" && !isPanning.current) {
      state.selectionManager.selectedObject = undefined;

      window.getSelection()?.removeAllRanges();
      (document.activeElement as HTMLElement)?.blur();

      page.resetIsNewOnAllConnections();
    }

    mouseIsDown.current = false;
    isPanning.current = false;
  };

  function setTransition() {
    if (pageRef.current) {
      (pageRef.current.style.transition as any) = `all .20s linear`;
    }
  }

  function removeTransition() {
    if (pageRef.current) {
      pageRef.current.style.transition = "none";
    }
  }

  function currentSize() {
    var size = {
      width: page.pagesize.width,
      height: page.pagesize.height,
    };

    if (!state.settings.showPageBorder) {
      size = {
        width: page.printableArea.width,
        height: page.printableArea.height,
      };
    }
    return size;
  }

  function calcMinZoom() {
    if (pageTypeInfo(page.documentType.type)?.calcMinZoom) {
      var size = currentSize();

      // if (!state.settings.showPageBorder) {
      //     size.width += page.printBorders.Right
      //     size.height += page.printBorders.Bottom
      // }

      size.width += shadowWrapperPadding * 2;
      size.height += shadowWrapperPadding * 2;

      // convert to pixel
      size.width *= cmToPixel;
      size.height *= cmToPixel;

      var w = pageOuterRef.current?.clientWidth;
      var h = pageOuterRef.current?.clientHeight;

      if (w && h) {
        size.width += shadowWrapperPadding * cmToPixel;
        size.height += shadowWrapperPadding * cmToPixel;
        const zoomHeight = h / size.height;
        const zoomWidth = w / size.width;

        if (Browser.isMobile()) {
          //
          // Cover x an y
          //
          if (h < w) {
            minZoom.current = zoomWidth;
          } else {
            minZoom.current = zoomHeight;
          }
        } else {
          //
          // Keep full page inside view
          //
          if (zoomHeight < zoomWidth) {
            minZoom.current = zoomHeight;
            logger.log("calcMinZoom via height", minZoom.current, h, size.height);
          } else {
            minZoom.current = zoomWidth;
            logger.log("calcMinZoom via width", minZoom.current, w, size.width);
          }
        }
      } else {
        minZoom.current = 0.222;
        logger.log("calcMinZoom FAILED");
      }
    }
    page.minZoom = minZoom.current || 1;
  }

  function thisPageIsActive(): boolean {
    let res = activePanes.getActiveId() == page.id;
    return res;
  }

  function limitOffset() {
    if (pageOuterRef.current) {
      const minLeft = 200; // * getCurrentZoomLevel();
      const minTop = 200; //  * getCurrentZoomLevel();

      const size = currentSize();
      size.width *= mmToPixel * 10;
      size.height *= mmToPixel * 10;

      const r = pageOuterRef.current.getBoundingClientRect();

      let maxRight = 200 + r.width / getCurrentZoomLevel();
      let maxBottom = 200 + r.height / getCurrentZoomLevel();

      const dx = size.width * getCurrentZoomLevel() - r.width;
      const dy = size.height * getCurrentZoomLevel() - r.height;

      maxRight = 200 + dx;
      maxBottom = 200 + dy;

      offset.current.x = Math.min(offset.current.x, minLeft);
      offset.current.y = Math.min(offset.current.y, minTop);

      offset.current.x = Math.max(offset.current.x, -maxRight);
      offset.current.y = Math.max(offset.current.y, -maxBottom);
    }
  }

  async function onWheelAdjustBackgroundImage(event: any) {
    if (page.adjustActive.value) {
      event.preventDefault();
      event.stopPropagation();

      const scaleWheel = event.ctrlKey ? event.deltaY / 100 : event.deltaY / 2000;
      const scaleFactor = 1 - scaleWheel;

      const room = page.parentRoom;

      if (page.isBackgroundImageFromFloorPlan && room?.roomtoFloorDrawingTransform) {
        const boundingRect = room.boundingRectInFloorSpace;
        let dx = boundingRect.width * scaleWheel;
        const sn = Math.sign(dx);
        dx = Math.abs(dx);
        dx = Math.min(Math.max(dx, 10), 100) * sn;

        const dy = (dx / boundingRect.width) * boundingRect.height;

        let newBoundaryRect = new BoundaryRect(
          new Point(boundingRect.min.x - dx, boundingRect.min.y - dy),

          new Point(boundingRect.max.x + dx, boundingRect.max.y + dy),
        );

        if (boundingRect.width < 200 || boundingRect.height < 200) {
          return;
        }

        page.freezeFloorPosition();
        room.boundingRectInFloorSpace = newBoundaryRect;
        room.roomtoFloorDrawingTransform.sizeAndPositionFromBoundingRectWithBorder(newBoundaryRect, page);
        page.unfreezeFloorPosition();

        page.parentRoom?.raiseOnChange();
      } else {
        page.backgroundImagePosition.scale *= scaleFactor;
        page.raiseOnChange("backgroundImagePosition");
      }
    }
  }

  function onWheelZoom(event: any) {
    console.log("onWheelZoom", event);

    if (pageRef.current && pageOuterRef.current?.contains(event.target)) {
      if (pageOuterRef.current) {
        // Enable wheel Zoom for Mouse with wheel
        //if(dialog is open)
        if (state.dialogManager.isOpen) {
          return;
        }

        // Heuristiken zur Unterscheidung Rad vs. Trackpad
        const isLikelyMouseWheel =
          // Mausrad: meist nur deltaY, oft ±100 oder ±120
          (Math.abs(event.deltaY) >= 100 && event.deltaX === 0) ||
          // Mausrad: deltaMode ist oft 1 (LINE) statt 0 (PIXEL)
          event.deltaMode === 1 ||
          // Mausrad: meist runde Werte wie 100, 120, 80
          (Math.abs(event.deltaY) % 20 === 0 && Math.abs(event.deltaY) >= 80);

        const isLikelyTrackpad =
          // Trackpad: oft deltaX UND deltaY gleichzeitig
          (Math.abs(event.deltaX) > 0 && Math.abs(event.deltaY) > 0) ||
          // Trackpad: meist kleinere, unrunde Werte
          (Math.abs(event.deltaY) < 100 && Math.abs(event.deltaY) % 1 !== 0) ||
          // Trackpad: deltaMode ist meist 0 (PIXEL)
          (event.deltaMode === 0 && Math.abs(event.deltaY) < 50);

        // console.log("onWheelZoom", event.wheelDelta, event);

        //
        // "Echte Wheel Events" haben ein WheelDelta von 120 (zumindestes in Chrome)
        // "TouchZoomGestures" werden wie das Scrollrad gehandhabt und haben haben ein WheelDelta von 120
        // "Falsche Wheel Events" haben eine WheelDelta der Anzahl der Pixel um die der Benutzer "schiebt"
        //
        const isWheel = event.ctrlKey || isLikelyMouseWheel;
        // (Math.abs(event.wheelDelta) >= 100 && Math.round(event.wheelDelta / 10) * 10 == event.wheelDelta);

        if (!isWheel) {
          //
          // use wheeelEvent for panning
          //
          if (pageOuterRef.current) {
            offset.current.x -= event.deltaX;
            offset.current.y -= event.deltaY;
            limitOffset();
            page.currentPan.value = new Point(pageOuterRef.current.scrollLeft, pageOuterRef.current.scrollTop);

            setTransformationInStyle();
            setSizeAndPositionForPageScroller();

            page.recalcConnections.value++;
          }
        } else {
          doWheelZoom(event);
        }
      }
    }
  }

  function doWheelZoom(event: any) {
    if (pageRef.current && pageOuterRef.current) {
      const p0 = new Point(event.clientX || event.touches[0]?.clientX, event.clientY || event.touches[0]?.clientY);

      const r = pageOuterRef.current.getBoundingClientRect();
      p0.x -= r.left;
      p0.y -= r.top;

      // dont allow default browser full page zoom

      const stepFactor = event.ctrlKey ? 100 : 500;
      let newZoomLevel = getCurrentZoomLevel() * (1 - (event.deltaY / stepFactor) * 0.5);

      if (newZoomLevel > minZoom.current * maxZoomFactor) newZoomLevel = minZoom.current * maxZoomFactor;
      if (newZoomLevel < minZoom.current) newZoomLevel = minZoom.current;

      if (zoomSliderRef.current) {
        (zoomSliderRef.current as any).value = zoomToSliderValue(newZoomLevel);
      }

      applyNewZoomLevel(p0, newZoomLevel);
    }
  }

  function applyNewZoomLevel(p0: Point, newZoomLevel: number) {
    if (pageRef.current && pageOuterRef.current && pageScrollerRef.current && pageScrollerContentRef.current) {
      page.recalcConnections.value++;

      const lastZoomLevel = getCurrentZoomLevel();

      ///////////////////////////////////////////////////////////

      const scroll_l = -offset.current.x;
      const scroll_t = -offset.current.y;

      const xScreenOld = p0.x + scroll_l;
      const xWorld = xScreenOld / lastZoomLevel;
      const xSceenNew = xWorld * newZoomLevel;

      const ySceenOld = p0.y + scroll_t;
      const yWorld = ySceenOld / lastZoomLevel;
      const yScreenNew = yWorld * newZoomLevel;

      let dx = xSceenNew - xScreenOld;
      let dy = yScreenNew - ySceenOld;

      if (newZoomLevel == minZoom.current) {
        //
        // Wenn wir schon ganz raus gezoomt haben
        // dann justieren wir ab dann die smooth auf die Initila-Darstellung
        //
        dx = 0;
        dy = 0;
        offset.current.x *= 0.5;
        offset.current.y *= 0.5;
      }

      offset.current.x -= dx;
      offset.current.y -= dy;

      ////////////////////////////

      pageRef.current.setAttribute("data-scale", `${newZoomLevel}`); // no itx
      setTransformationInStyle();

      if (zoomFrameRef.current !== undefined) {
        cancelAnimationFrame(zoomFrameRef.current);
      }
      zoomFrameRef.current = requestAnimationFrame(() => {
        zoomFrameRef.current = undefined;
        setSizeAndPositionForPageScroller();
        setZoom(newZoomLevel);
      });
    }
  }

  function setSizeAndPositionForPageScroller() {
    if (pageScrollerRef.current && pageScrollerContentRef.current) {
      const size = currentSize();
      size.width *= mmToPixel * 10;
      size.height *= mmToPixel * 10;

      pageScrollerRef.current.scrollLeft = -offset.current.x;
      pageScrollerRef.current.scrollTop = -offset.current.y;
      pageScrollerContentRef.current.style.width = size.width * getCurrentZoomLevel() + "px";
      pageScrollerContentRef.current.style.height = size.height * getCurrentZoomLevel() + "px";
    }
  }

  function setTransformationInStyle() {
    if (pageRef.current && pageOuterRef.current) {
      const currentZoomLevel = getCurrentZoomLevel();
      pageRef.current.style.transform = `translate(${offset.current.x}px, ${offset.current.y}px) scale(${currentZoomLevel})`;
    }
  }

  function onWheel(event: any) {
    if (!thisPageIsActive()) {
      return;
    }

    for (const el of document.elementsFromPoint(event.clientX, event.clientY)) {
      if (el.closest("#cabinet, #sceneGroupView, #sceneGroupViewContent, .sceneGroups")) {
        return;
      }
    }

    if (page.adjustActive.value) {
      onWheelAdjustBackgroundImage(event);
    } else {
      event.preventDefault();
      event.stopPropagation();

      onWheelZoom(event);
    }
  }

  function SheetSpinner(props: {}) {
    useEffect(() => {
      if (sheetNumberRef.current) {
        sheetNumberRef.current.value = sheetNumber.toString();
      }
    });

    function onChangeSheetNumber(ev: any) {
      const inputnumber = parseInt(ev.target.value);
      if (inputnumber > 0 && inputnumber <= page.numberOfSheets) {
        setSheetNumber(parseInt(ev.target.value));
      } else {
        ev.target.value = sheetNumber;

        showToast({
          text: state.translate("PageView.invalidNumber", "Invalid number"),
        });
      }
    }

    if (!isSsheetSpinnerVisible()) {
      return null;
    }

    function getToRightWidth(): string {
      if (Browser.isMobile()) {
        return "15px"; // no itx
      }
      let res = 20;
      // const toolbarElement = document.getElementsByClassName("toolbar");

      // if (toolbarElement[0]) {
      //   res += (toolbarElement[0] as any).offsetWidth;
      // }
      const zoomSliderElement = document.getElementById("zoomSlider");
      if (zoomSliderElement) {
        res += zoomSliderElement.offsetWidth;
      }

      return `${res}px`;
    }
    let extraStyle: any = {};
    if (Browser.isMobile()) {
      extraStyle = {
        backgroundColor: "white",
        border: "1px solid lightgrey",
        borderRadius: "5px",
        padding: "5px",
        bottom: "50px",
        right: "20px", // no itx
        height: "35px",
      };
    }

    return (
      <div
        id="sheetNumber"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "row",
          alignContent: "center",
          alignItems: "center",
          marginRight: "10px",
          ...extraStyle,
        }}
      >
        <div
          className="arrowSheetNumber"
          onClick={() => {
            if (sheetNumber > 1) {
              setSheetNumber(sheetNumber - 1);
            }
          }}
        >
          <i className="las la-angle-left"></i>
        </div>
        <input
          className="input inputPageNumber"
          style={{
            textAlign: "center",
            height: "20px",
            width: "30px",
            marginTop: "2px",
            fontSize: "13px",
            paddingLeft: "0",
          }}
          type="number" // no itx
          ref={sheetNumberRef}
          min={1}
          max={page.numberOfSheets}
          defaultValue={1}
          onBlur={onChangeSheetNumber}
        />
        <div
          className="arrowSheetNumber"
          onClick={() => {
            if (sheetNumber < page.numberOfSheets) {
              setSheetNumber(sheetNumber + 1);
            }
          }}
        >
          <i className="las la-angle-right"></i>
        </div>
      </div>
    );
  }

  function isSsheetSpinnerVisible(): boolean {
    if (pageTypeInfo(page.documentType.type)?.isList) {
      return true;
    }
    return false;
  }

  function zoomToSliderValue(newValue: number): number {
    if (minZoom.current) {
      return (newValue / minZoom.current) * 100;
    } else return 0;
  }

  function scaleToSliderValue(scale: number): number {
    if (scale > 1) {
      return (scale / 5) * 100 - 100;
    } else {
      return (1 - scale * 5) * 100;
    }
  }

  function ZoomSlider(props: {}) {
    useEffect(() => {
      if (zoomSliderRef.current) {
        zoomSliderRef.current.value = zoomToSliderValue(getCurrentZoomLevel()).toString();
      }
    });

    function onChangeSliderZoom(ev: any) {
      var vOrg = ev.target.value;

      var newZoomLevel = vOrg / 100;
      newZoomLevel = minZoom.current * newZoomLevel;

      if (newZoomLevel < minZoom.current) {
        newZoomLevel = minZoom.current;
      }

      if (newZoomLevel == minZoom.current) {
        offset.current.x = 0;
        offset.current.y = 0;
      }

      if (pageOuterRef.current) {
        applyNewZoomLevel(
          new Point(pageOuterRef.current?.clientWidth / 2, pageOuterRef.current?.clientHeight / 2),
          newZoomLevel,
        );
      }
    }

    if (Browser.isMobile()) {
      return null;
    }
    return (
      <input
        id="zoomSlider"
        style={{
          width: "50px",
          flexGrow: "1",
          height: "30px",
        }}
        type="range"
        ref={zoomSliderRef}
        min={100}
        max={100 * maxZoomFactor}
        defaultValue={100}
        onChange={onChangeSliderZoom}
      />
    );
  }

  function setZoom(newZoomValue: number) {
    if (pageRef.current) {
      //set the boundary conditions of the view of display
      // if (v < minZoom.current) {
      //   v = minZoom.current;
      // }

      pageRef.current.setAttribute("data-scale", `${newZoomValue}`); // no itx

      page.currentZoom.value = newZoomValue;

      checkDockCabinetView();

      const size = currentSize();

      if (pageScrollerContentRef.current) {
        const bugFix = 5 / cmToPixel;
        pageScrollerContentRef.current.style.width = `${size.width * newZoomValue + shadowWrapperPadding * 2 - bugFix}cm`;
        pageScrollerContentRef.current.style.height = `${size.height * newZoomValue + shadowWrapperPadding * 2 - bugFix}cm`;
      }
    }
  }

  function checkDockCabinetView() {
    if (page.currentZoom.value > page.minZoom || Math.abs(offset.current.x) > 10) {
      page.fixedCabinetView.value = true;
    } else {
      page.fixedCabinetView.value = false;
    }
  }

  function copyAllStyles(fromDocument: Document, toDocument: Document) {
    logger.log("copyAllStyles", fromDocument.styleSheets);
    const toHead = toDocument.querySelector("head"); // no itx

    if (toHead) {
      // at src document
      const styleSheets = Array.from(document.styleSheets);
      styleSheets.forEach((cssStyleSheet) => {
        const newStyle = toDocument.createElement("style");

        const newSheet = newStyle.sheet;
        if (true || newSheet) {
          const rules: CSSRuleList = cssStyleSheet.cssRules;
          var index = 0;
          for (let i in rules) {
            try {
              const cssText = rules[i].cssText;
              // newSheet.insertRule(cssText, index++)
              newStyle.append(cssText);
            } catch (ex) {
              logger.error(ex);
            }
          }
          toHead.append(newStyle);
        }
      });
    }
  }

  var pageStyle: any = {
    position: "absolute",

    transformOrigin: "top left", // no itx
    transform: `scale(${1})`,

    // transition: `all .20s linear`
  };

  if (state.settings.showPageBorder) {
    pageStyle = {
      ...pageStyle,
      position: "absolute",

      paddingLeft: `${page.printBorders.Left}cm`,
      paddingRight: `${page.printBorders.Right}cm`,
      paddingBottom: `${page.printBorders.Bottom}cm`,
      paddingTop: `${page.printBorders.Top}cm`,

      width: `${page.pagesize.width}cm`,
      height: `${page.pagesize.height}cm`,
    };
  } else {
    pageStyle = {
      ...pageStyle,
      width: `${page.printableArea.width}cm`,
      height: `${page.printableArea.height}cm`,
    };
  }

  var pageShadowWrapperStyle: any = {
    position: "absolute",
    left: "0px",
    top: "0px",
    padding: `${shadowWrapperPadding * cmToPixel}px`,
    width: `${(page.pagesize.width + shadowWrapperPadding) * 2 * cmToPixel - 50}px`,
    height: `${(page.pagesize.height + shadowWrapperPadding) * 2 * cmToPixel - 50}px`,
  };

  if (!state.settings.showPageBorder) {
    pageShadowWrapperStyle = {
      ...pageShadowWrapperStyle,
      width: `${page.printableArea.width + shadowWrapperPadding * 2}cm`,
      height: `${page.printableArea.height + shadowWrapperPadding * 2}cm`,
    };
  }

  const isVisible =
    activePanes.activeItemIndex.value >= 0 && activePanes.items[activePanes.activeItemIndex.value].item === page;

  const diagnosticMode = state.settings.diagnosticMode;

  return (
    <div
      id={`pane_${page.id}`}
      className="pane"
      style={{
        position: "absolute",
        left: "0px",
        top: "0px",
        width: "100%",
        height: " calc(100% - 5px)",
        padding: "5px",
        display: "flex",
        visibility: isVisible ? "visible" : "hidden",
        animation: "fadeInAnimation ease 1s",
        animationIterationCount: "1",
        animationFillMode: "forwards",
        touchAction: "none",
      }}
      ref={touchZoomRef}
      onTouchMove={(ev: any) => {
        const gesture = Zoomer.touchMove(ev);
        if (gesture) {
          let v = getCurrentZoomLevel() * gesture.scale;
          if (v > 5) v = maxZoomFactor;
          if (v < minZoom.current) v = minZoom.current;

          setZoom(v);
          if (zoomSliderRef.current) {
            (zoomSliderRef.current as any).value = scaleToSliderValue(v);
          }

          const dx = gesture.translation.x;
          const dy = gesture.translation.y;

          pageOuterRef.current?.scrollBy({
            top: -dy,
            left: -dx,
            // behavior: "smooth",
          });
        }
      }}
    >
      <div
        id="pageContainer"
        style={{
          position: "relative",
          width: "100%",
          height: "100%",
          borderRadius: "var(--panel-border-radius)",
          boxShadow: "var(--panel-shadow)",
        }}
        ref={pageOuterRef}
        onPointerDown={handlePanPointerDown}
        onPointerMove={handlePanPointerMove}
        onPointerUp={handlePanPointerUp}
        onClick={onClick}
      >
        <div
          id="pageScroller"
          ref={pageScrollerRef}
          onMouseDown={(ev) => {
            pageScrollerMouseDown.current = true;
          }}
          onMouseUp={(ev) => {
            pageScrollerMouseDown.current = false;
          }}
          onScroll={(ev) => {
            if (pageScrollerRef.current && pageScrollerMouseDown.current) {
              // page.currentPan.value = new Point(pageOuterRef.current.scrollLeft, pageOuterRef.current.scrollTop);
              offset.current.x = -pageScrollerRef.current.scrollLeft;
              offset.current.y = -pageScrollerRef.current.scrollTop;
              setTransformationInStyle();
              page.recalcConnections.value++;
            }
          }}
          style={{
            position: "relative",
            width: "100%",
            height: "100%",
            overflow: "scroll",
            touchAction: "none",
          }}
        >
          <div
            id="pageScrollerContent"
            ref={pageScrollerContentRef}
            style={{
              position: "relative",
              width: "2000px",
              height: "1000px",
            }}
          >
            {/* This is the content of the page scroller */}
          </div>
        </div>

        <div
          id="pageOuter"
          ref={pageClipperRef}
          style={{
            position: "absolute",
            left: "0px",
            top: "0px",
            width: "100%",
            height: "100%",
            paddingRight: "40px",
            paddingBottom: "40px",
            touchAction: "none",
          }}
        >
          <div id="pageShadowWrapper" ref={pageShadowWrapperRef} style={pageShadowWrapperStyle}>
            <div
              id={page.id}
              ref={pageRef}
              className={"page"} // no itx
              style={pageStyle}
              data-scale={1}
            >
              {/* zoom transformation is applied here ! */}
              <RenderTargetProvider value={RenderTarget.web}>
                <PageFrame page={page}>
                  <div
                    draggable="false" // no itx
                    style={{
                      position: "relative",
                      width: "100%",
                      height: "100%",
                    }}
                  >
                    <div
                      id="documumentActiveArea"
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                      }}
                    >
                      <PageContent page={page} />
                    </div>
                  </div>
                </PageFrame>

                {/* <DebugGrid /> */}
              </RenderTargetProvider>
            </div>
          </div>
        </div>

        <DrawingItemEditContainer page={page} />

        {page.fixedCabinetView.value && <CabinetView mainPage={page} fixed={true} />}

        {page.fixedCabinetView.value && !diagnosticMode && state.sceneGroup && (
          <div className="sceneGroups" style={{ position: "absolute", right: "0px", top: "0px", bottom: "60px" }}>
            <SceneGroupView
              fixed={false}
              mainPage={page}
              sceneGroup={state.sceneGroup}
              adjustActive={page.adjustActive.value}
            />
          </div>
        )}

        <AdjustBackgroundImage page={page} />
      </div>

      <div style={{ position: "absolute", left: "10px", top: "10px" }}>
        <ButtonChannelsView page={page} />
      </div>

      <div
        id="pageControl"
        style={{
          position: "absolute",
          right: `5px`,
          bottom: "0px",
          width: Browser.isMobile() ? "70px" : "150px", // no itx
          height: "30px",
          transform: "translate(0px, 27px)",
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "right",
          zIndex: "1",
        }}
      >
        <SheetSpinner />
        <ZoomSlider />
      </div>
    </div>
  );
}
